import { useEffect, useMemo, useState } from 'react';
import { User, X } from 'lucide-react';
import { useAuth, hasRole } from './auth';
import { api } from './api';
import { useI18n } from './i18n';

/**
 * Vista por persona en cada sección: por defecto cada usuario ve sus claims (su
 * nombre en Monday = monday_assignee, o su `name`); puede ver "All" o elegir a OTRO
 * miembro del equipo para ver su vista (con buscador). Pay Queue y Payment
 * Authorization no usan esto (ven todos).
 */
export function useMyName(): string {
  const { me } = useAuth();
  if (me?.kind !== 'tenant') return '';
  return (me.user.monday_assignee || me.user.name || '').trim();
}

/**
 * ¿Un nombre asignado (token) corresponde a `me`? Match por PALABRA completa: "Manuel"
 * matchea "Manuel Ramirez" y viceversa, pero "Ana" NO matchea "Susana" (evita el
 * sobre-conteo por substring). Igualdad exacta o el nombre más corto está contenido
 * palabra-por-palabra en el más largo.
 */
function nameTokenMatch(token: string, me: string): boolean {
  if (!token || !me) return false;
  if (token === me) return true;
  const tw = token.split(/\s+/).filter(Boolean);
  const mw = me.split(/\s+/).filter(Boolean);
  if (!tw.length || !mw.length) return false;
  const [short, long] = tw.length <= mw.length ? [tw, mw] : [mw, tw];
  return short.every((w) => long.includes(w));
}

/** ¿El `assigned_person` (puede venir con varios separados por coma) incluye el nombre? */
export function matchesAssignee(assigned: string | null | undefined, name: string): boolean {
  const me = name.trim().toLowerCase();
  if (!me) return false;
  return (assigned ?? '')
    .toLowerCase()
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean) // un claim SIN asignar no matchea a nadie (antes '' matcheaba a todos → "585 para todos")
    .some((s) => nameTokenMatch(s, me));
}

export type Scope = 'mine' | 'all' | 'member';

/**
 * Filtra `items` por persona. `scope`: mine (yo) / all (todos) / member (otro
 * miembro elegido). Devuelve la lista visible, contadores y los setters.
 */
export function useScope<T>(
  items: T[],
  getAssignee: (x: T) => string | null | undefined,
  opts?: {
    department?: string | null;
    /**
     * COLAS DE TRABAJO COMPARTIDO (Review Invoice, cheques, Owed to us): si el usuario no tiene
     * NINGUNO asignado pero la cola SÍ tiene trabajo, se le muestra todo en vez de una pantalla
     * vacía. Erika abría "Review Invoice 17" y veía "No claims in this queue right now" porque
     * ninguno de los 17 estaba a su nombre.
     * El To Do NO lo usa: ahí cada quien ve solo lo suyo, que es justo lo que se pidió.
     */
    fallbackToAll?: boolean;
    /**
     * EL BUSCADOR DE LA PÁGINA, para que los botones de scope no cuenten de más.
     *
     * Cada pantalla aplicaba su búsqueda DESPUÉS, sobre `sc.shown`, así que `Mía (N)` y
     * `All (N)` seguían contando la cola entera: escribías "Smith", quedaban 2 filas y los
     * botones seguían diciendo 40. Pasando el predicado acá, el scope y su contador miran lo
     * mismo que la lista.
     *
     * Es opcional a propósito: quien no lo pasa se comporta igual que siempre.
     */
    filtro?: (x: T) => boolean;
    /**
     * EL CLAIM TEAM del item (pedido 2026-08-19: "que se pueda filtrar por claim team").
     * Con esto ScopeTabs muestra un desplegable de equipos y el filtro se aplica ANTES del
     * scope por persona — los contadores Mía/All cuentan dentro del equipo elegido.
     */
    getTeam?: (x: T) => string | null | undefined;
  },
) {
  const myName = useMyName();
  const { me } = useAuth();
  // MANAGERS arrancan en "All" (regla de Manuel 2026-08-19: "por defecto cada quien ve lo
  // suyo, menos managers que ven todo"). El resto arranca en lo suyo y puede cambiar.
  const mgr = hasRole(me, 'manager');
  const [scope, setScope] = useState<Scope>(mgr ? 'all' : 'mine');
  const [member, setMember] = useState(''); // assignee del miembro elegido
  const [team, setTeam] = useState(''); // '' = todos los equipos | nombre exacto del claim team

  /* La base de TODO lo que se cuenta y se muestra: los items ya pasados por el buscador de la
     página (si lo pasó) y por el filtro de CLAIM TEAM. El scope por persona se aplica encima. */
  const filtro = opts?.filtro;
  const getTeam = opts?.getTeam;
  const preBase = filtro ? items.filter(filtro) : items;
  const base = getTeam && team ? preBase.filter((x) => (getTeam(x) ?? '') === team) : preBase;

  // Equipos presentes en la cola cargada (lista estable, sin filtros encima).
  const teamOptions = useMemo<string[]>(() => {
    if (!getTeam) return [];
    const set = new Map<string, string>();
    for (const x of items) {
      const nm = (getTeam(x) ?? '').trim();
      if (nm) set.set(nm.toLowerCase(), nm);
    }
    return [...set.values()].sort((a, b) => a.localeCompare(b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const myCountRaw = useMemo(
    () => base.filter((x) => matchesAssignee(getAssignee(x), myName)).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [base, myName],
  );
  // Nada mío pero la cola tiene trabajo → se muestra todo (solo donde se pidió el fallback).
  const emptyForMe = !!opts?.fallbackToAll && scope === 'mine' && myCountRaw === 0 && base.length > 0;
  const filterName = emptyForMe ? null : scope === 'mine' ? myName : scope === 'member' ? member : null;
  const shown = useMemo(
    () => (filterName ? base.filter((x) => matchesAssignee(getAssignee(x), filterName)) : base),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [base, filterName],
  );
  const myCount = myCountRaw;

  // Miembros de ESTA vista/departamento: nombres que realmente aparecen como
  // "assigned person" en los items cargados (no todo el personal). Un assignee
  // puede traer varios nombres separados por coma. Ordenados y sin duplicados.
  const memberOptions = useMemo<Member[]>(() => {
    const set = new Map<string, string>(); // lower -> display
    for (const x of items) {
      const raw = getAssignee(x) ?? '';
      for (const part of raw.split(',')) {
        const name = part.trim();
        if (name) set.set(name.toLowerCase(), name);
      }
    }
    return [...set.values()]
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ id: name, name, assignee: name }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // Con `department`, la lista del selector es la GENTE DE ESE DEPARTAMENTO (aunque hoy no tenga
  // nada asignado): así en Intake salen Gigi/Viviana y en Scheduling su equipo, y en Claims ya no
  // aparece gente de Payments. Se unen los nombres que sí aparecen en los items.
  const [deptMembers, setDeptMembers] = useState<Member[]>([]);
  useEffect(() => {
    const key = opts?.department;
    if (!key) { setDeptMembers([]); return; }
    let alive = true;
    api.get<{ members: Member[] }>(`/api/team/members?department=${encodeURIComponent(key)}`)
      .then((r) => { if (alive) setDeptMembers(r.members || []); })
      .catch(() => { if (alive) setDeptMembers([]); });
    return () => { alive = false; };
  }, [opts?.department]);

  // Con departamento la lista es EXCLUSIVAMENTE su gente: en Intake salen Gigi y Viviana y nadie
  // más. NO se unen los nombres que aparecen en los items — esos vienen de `assigned_person`, que
  // el sync de Monday llena con gente de Payments, y era justo lo que contaminaba el selector.
  const options = useMemo<Member[]>(() => {
    if (!opts?.department) return memberOptions;
    return [...deptMembers].sort((a, b) => a.name.localeCompare(b.name));
  }, [deptMembers, memberOptions, opts?.department]);

  return {
    scope, setScope, member, setMember, myName, shown, myCount,
    allCount: base.length, memberOptions: options,
    /** Filtro por CLAIM TEAM (solo si el llamador pasó getTeam). */
    team, setTeam, teamOptions,
    /** true = se está mostrando todo porque el usuario no tiene nada asignado en esta cola. */
    showingAllBecauseEmpty: emptyForMe,
  };
}

type Member = { id: string; name: string; assignee: string };

/**
 * Pestañas "Mi nombre / All" + selector de otro miembro (buscable). Recibe el estado
 * de useScope por spread.
 */
export function ScopeTabs({
  scope,
  setScope,
  member,
  setMember,
  myName,
  myCount,
  allCount,
  memberOptions,
  shown,
  admin = true,
  showingAllBecauseEmpty,
  team,
  setTeam,
  teamOptions,
}: {
  scope: Scope;
  setScope: (s: Scope) => void;
  member: string;
  setMember: (m: string) => void;
  myName: string;
  myCount: number;
  allCount: number;
  memberOptions?: Member[];
  shown?: unknown[]; // lista ya filtrada → cuenta del miembro elegido en el botón
  /** Se está mostrando todo porque el usuario no tiene nada asignado en esta cola. */
  showingAllBecauseEmpty?: boolean;
  /** Filtro por CLAIM TEAM (viene de useScope cuando el llamador pasó getTeam). */
  team?: string;
  setTeam?: (t: string) => void;
  teamOptions?: string[];
  /**
   * Quién puede ver "All" y la vista de otra persona. Por DEFECTO true: así se comporta como
   * siempre en todas las colas (Review Invoice, cheques, Owed to us…). Solo el To Do lo pone en
   * false, porque allí esos dos controles viven arriba y son exclusivos del super-manager.
   * OJO: ponerlo en false por defecto dejó a esas ocho pantallas sin el botón "All" — y como el
   * filtro arranca en "lo mío", quien no fuera el asignado veía la cola vacía.
   */
  admin?: boolean;
}) {
  const { t } = useI18n();
  const { me } = useAuth();
  // Al super-manager no se le muestra su propio nombre: nunca tiene nada a su nombre y la
  // pestaña solo servía para dejarlo mirando una lista vacía.
  const boss = hasRole(me, 'super-manager');
  if (!myName) return null;
  const firstName = myName.split(' ')[0] || myName;

  // Desplegable de CLAIM TEAM (solo si esta cola trae el dato): todos pueden filtrar por
  // equipo, incluido el que ya está en su vista personal.
  const teamSelect = setTeam && (teamOptions?.length ?? 0) >= 1 ? (
    <select className="input sm" style={{ maxWidth: 200 }} value={team ?? ''} onChange={(e) => setTeam(e.target.value)} title={t('scope.claimTeam')}>
      <option value="">{t('scope.claimTeam')}: {t('cr.filter.all')}</option>
      {teamOptions!.map((x) => <option key={x} value={x}>{x}</option>)}
    </select>
  ) : null;

  // Sin permisos de super-manager: solo su propio nombre (ni "ver a otro"). Los managers
  // conservan el botón All: arrancan en "todo" (regla 2026-08-19) y sin el botón, al tocar
  // su nombre no tendrían cómo volver.
  if (!admin) {
    const mgr = hasRole(me, 'manager');
    return (
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className={`btn sm${scope === 'mine' ? '' : ' ghost'}`} onClick={() => setScope('mine')}>{firstName} ({myCount})</button>
        {mgr && (
          <button className={`btn sm${scope === 'all' ? '' : ' ghost'}`} onClick={() => setScope('all')}>
            All ({allCount})
          </button>
        )}
        {teamSelect}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
      {!boss && (
        <button className={`btn sm${scope === 'mine' ? '' : ' ghost'}`} onClick={() => setScope('mine')}>
          {firstName} ({myCount})
        </button>
      )}
      {/* Se dice por qué se ve todo: no hay nada a tu nombre en esta cola. */}
      {showingAllBecauseEmpty && (
        <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('scope.noneMineShowingAll')}</span>
      )}
      <button className={`btn sm${scope === 'all' ? '' : ' ghost'}`} onClick={() => setScope('all')}>
        All ({allCount})
      </button>
      <MemberSelect
        active={scope === 'member'}
        selected={member}
        selectedCount={scope === 'member' ? (shown?.length ?? 0) : undefined}
        options={memberOptions}
        onSelect={(m) => { setMember(m.assignee); setScope('member'); }}
        onClear={() => { setMember(''); setScope('mine'); }}
        placeholder={t('scope.viewMember')}
      />
      {teamSelect}
    </div>
  );
}

/**
 * Dropdown buscable de miembros (para ver la vista de otro). Si recibe `options`
 * (los assignees presentes en ESTA vista/departamento) los usa; si no, cae al
 * endpoint global de personal (compatibilidad).
 */
function MemberSelect({
  active, selected, selectedCount, options, onSelect, onClear, placeholder,
}: {
  active: boolean;
  selected: string;
  selectedCount?: number;
  options?: Member[];
  onSelect: (m: Member) => void;
  onClear: () => void;
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [fetched, setFetched] = useState<Member[]>([]);
  const [loaded, setLoaded] = useState(false);
  const useLocal = Array.isArray(options);
  const members = useLocal ? options! : fetched;

  useEffect(() => {
    if (open && !useLocal && !loaded) {
      // Gente + EQUIPOS (pedido del dueño en Claims: "déjame ver teams, no solo individuals").
      // Un equipo filtra por su nombre — el haystack de Claims incluye los teams asignados.
      Promise.all([
        api.get<{ members: Member[] }>('/api/team/members').then((r) => r.members).catch(() => [] as Member[]),
        api.get<{ teams: Array<{ id: string; name: string; is_individual: boolean }> }>('/api/teams/options')
          .then((r) => r.teams.filter((tm) => !tm.is_individual).map((tm) => ({ id: `team-${tm.id}`, name: tm.name, assignee: tm.name })))
          .catch(() => [] as Member[]),
      ]).then(([people, teams]) => { setFetched([...teams, ...people]); setLoaded(true); });
    }
  }, [open, loaded, useLocal]);

  // Esc cierra el modal (el backdrop se encarga del click afuera).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const ql = q.trim().toLowerCase();
  const filtered = ql ? members.filter((m) => m.name.toLowerCase().includes(ql) || m.assignee.toLowerCase().includes(ql)) : members;

  if (active && selected) {
    return (
      <button className="btn sm" style={{ background: '#0d9488' }} onClick={onClear} title="Volver a mi vista">
        <User size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {selected}{selectedCount != null ? ` (${selectedCount})` : ''} <X size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} />
      </button>
    );
  }

  return (
    <>
      <button className="btn ghost sm" onClick={() => setOpen(true)}><User size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {placeholder} ▾</button>
      {/* MODAL CENTRADO (pedido del dueño 2026-08-08): el dropdown quedaba por detrás de las
          cards del board en el teléfono. Ahora es una ventana emergente al medio, con backdrop;
          se cierra al elegir, al tocar afuera o con Esc. */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 4000, background: 'rgba(15,23,42,.45)',
            display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: '12vh' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: 'min(420px, 92vw)', maxHeight: '68vh', display: 'flex', flexDirection: 'column',
              background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 14,
              boxShadow: '0 24px 60px rgba(0,0,0,.28)', padding: 10 }}
          >
            <input className="input" autoFocus placeholder={placeholder} value={q} onChange={(e) => setQ(e.target.value)} style={{ width: '100%', marginBottom: 6 }} />
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
              {!useLocal && !loaded ? (
                <div className="muted" style={{ fontSize: 'var(--fs-2xs)', padding: 6 }}>…</div>
              ) : filtered.length === 0 ? (
                <div className="muted" style={{ fontSize: 'var(--fs-2xs)', padding: 6 }}>—</div>
              ) : (
                filtered.map((m) => (
                  <button
                    key={m.id}
                    className="btn ghost sm"
                    style={{ display: 'block', width: '100%', textAlign: 'left', minHeight: 40 }}
                    onClick={() => { onSelect(m); setOpen(false); setQ(''); }}
                  >
                    {m.name}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
